import React, { useState, useEffect } from 'react';
import { Alert } from '../../utils/Alert';
import { asList } from '../../utils/apiList';
import axios from 'axios';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AdminSidebar from './AdminSidebar';
import './Admin.css';
import ReportDetailsModal from '../../components/ui/ReportDetailsModal';
import { FaClipboardList } from 'react-icons/fa';


import { API_URL } from '../../config';
interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  full_name: string;
  created_at: string;
  updated_at: string | null;
  last_login: string | null;
}

// New interfaces for the updated API response
interface Attempt {
  attempt_number: number;
  score_percentage: number;
  obtained_marks: number;
  max_marks: number;
  total_questions: number;
  correct_answers: number;
  passed: boolean;
  attempt_date: string;
}

interface ExamReport {
  exam_id: number;
  exam_title: string;
  student_id: number;
  student_name: string;
  total_attempts: number;
  avg_score: number;
  best_score: number;
  attempts_remaining: number;
  max_attempts: number;
  attempts: Attempt[];
  improvement_percentage: number | null;
}

interface DashboardPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
  };
  onLogout: () => void;
}

const StudentResults: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const [students, setStudents] = useState<User[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [examReports, setExamReports] = useState<ExamReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<any>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<any>(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          Alert.error('Oops...', 'Authentication token not found');
          return;
        }

        const response = await axios.get(`${API_URL}/api/users/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          },
          params: { role: 'student', limit: 100000 }
        });

        // Filter only student users
        const usersData = (response.data as any).items || response.data || [];
        const studentUsers = usersData.filter((user: User) => user.role === 'student');
        setStudents(studentUsers);
      } catch (err: any) {
        console.error('Error fetching students:', err);
        Alert.error('Oops...', 'Failed to fetch students');
      }
    };

    fetchStudents();
  }, []);

  const fetchStudentResults = async (studentId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        Alert.error('Oops...', 'Authentication token not found');
        return;
      }

      // Use the new API endpoint
      const response = await axios.get(
        `${API_URL}/api/reports/student/${studentId}/attempts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        }
      );

      setExamReports(asList(response.data));
    } catch (err: any) {
      console.error('Error fetching student results:', err);
      Alert.error('Oops...', 'Failed to fetch student results');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const studentId = parseInt(e.target.value, 10);
    if (studentId) {
      const student = students.find(s => s.id === studentId);
      if (student) {
        setSelectedStudent(studentId);
        setSelectedStudentName(student.full_name || student.username);
        fetchStudentResults(studentId);
      }
    } else {
      // Selected the default "Select a student" option
      setSelectedStudent(null);
      setSelectedStudentName('');
    }
  };

  const handleClearSelection = () => {
    setSelectedStudent(null);
    setSelectedStudentName('');
  };

  // Add back the formatDate function
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate total stats across all exams
  const calculateTotalStats = () => {
    if (!examReports.length) return { total: 0, passed: 0, failed: 0, avgScore: 0 };
    
    let totalExams = examReports.length;
    let passedExams = examReports.filter(report => report.best_score >= 50).length; // Assuming 50% is pass threshold
    let failedExams = totalExams - passedExams;
    let avgScore = examReports.reduce((sum, report) => sum + report.best_score, 0) / totalExams;
    
    return {
      total: totalExams,
      passed: passedExams,
      failed: failedExams,
      avgScore
    };
  };

  const stats = calculateTotalStats();

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<AdminSidebar />}>
      <div className="min-h-screen bg-gray-50 px-6 py-8 font-kumbh">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-start gap-2">
            <div>
              <h1 className="admin-title">Student Exam Results</h1>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md shadow-sm" role="alert">
            <div className="flex">
              <div className="py-1">
                <svg className="fill-current h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 10 4.34 4.34a8 8 0 0011.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/>
                </svg>
              </div>
              <div>
                <p className="font-bold">Error</p>
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Simple Student Selector with only dropdown, no search */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select a Student</h2>
            
            <div className="space-y-4">
              {/* Standard select dropdown */}
              <div>
                <select
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700 focus:border-blue-500 bg-white text-gray-900"
                  value={selectedStudent || ""}
                  onChange={handleStudentSelect}
                >
                  <option value="">Select a student to view results</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.full_name || student.username} ({student.email})
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedStudent && (
                <div className="flex justify-end mt-4">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="text-[#111827] hover:bg-blue-50 border-none shadow-none font-medium focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-150"
                    onClick={handleClearSelection}
                  >
                    <span className="inline-flex items-center">
                      <svg className="h-4 w-4 mr-1.5 text-[#111827]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear Selection
                    </span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Results Section */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-sm">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
          </div>
        ) : selectedStudent ? (
          examReports.length > 0 ? (
            <div className="space-y-6">
              {/* Statistics Summary */}
              <Card className="overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900">Performance Summary</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <h3 className="text-sm font-medium text-blue-800 ">Total Exams</h3>
                      <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                      <h3 className="text-sm font-medium text-green-800 ">Passed</h3>
                      <p className="text-2xl font-bold text-green-900">{stats.passed}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                      <h3 className="text-sm font-medium text-red-800 ">Failed</h3>
                      <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <h3 className="text-sm font-medium text-purple-800 ">Average Best Score</h3>
                      <p className="text-2xl font-bold text-purple-900">{stats.avgScore.toFixed(2)}%</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Detailed Results - Now in an accordion-like UI */}
              <div className="space-y-4">
                {examReports.map((report) => (
                  <Card key={report.exam_id} className="overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-200">
                    <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex justify-between items-center">
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {report.exam_title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                          <span className="flex items-center">
                            <svg className="h-4 w-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Total Attempts: {report.total_attempts} / {report.max_attempts}
                          </span>
                          <span className="flex items-center">
                            <svg className="h-4 w-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Best Score: {report.best_score}%
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            report.best_score >= 50 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {report.best_score >= 50 ? 'Passed' : 'Failed'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-600 mb-3">Score Overview</h4>
                            <div className="relative pt-1">
                              <div className="flex mb-2 items-center justify-between">
                                <div>
                                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-[#111827] bg-blue-200">
                                    Best Score
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xs font-semibold inline-block text-[#111827]">
                                    {report.best_score}%
                                  </span>
                                </div>
                              </div>
                              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                                <div 
                                  style={{ width: `${report.best_score}%` }}
                                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                    report.best_score >= 70 ? 'bg-green-500' : 
                                    report.best_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                ></div>
                              </div>
                            </div>

                            {report.improvement_percentage !== null && (
                              <div className="mt-4">
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-600 mr-2">Improvement: </span>
                                  <span className={`text-sm font-medium ${
                                    report.improvement_percentage > 0 
                                      ? 'text-green-600' 
                                      : report.improvement_percentage < 0 
                                        ? 'text-red-600' 
                                        : 'text-gray-600'
                                  }`}>
                                    {report.improvement_percentage > 0 ? '+' : ''}
                                    {report.improvement_percentage}%
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="text-sm font-medium text-gray-600">Avg. Score</div>
                              <div className="mt-1 text-2xl font-semibold text-[#111827]">{report.avg_score}%</div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="text-sm font-medium text-gray-600">Attempts</div>
                              <div className="mt-1 text-2xl font-semibold text-purple-600">
                                {report.total_attempts} / {report.max_attempts}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-3">Attempt History</h4>
                          <div className="overflow-x-auto max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                                    #
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                                    Score
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                                    Marks
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                                    Date
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap w-12">
                                    View
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {report.attempts.map((attempt) => (
                                  <tr
                                    key={attempt.attempt_number}
                                    className="hover:bg-blue-50 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                                    role="button"
                                    tabIndex={0}
                                    title={`View Attempt #${attempt.attempt_number} details`}
                                    aria-label={`View Attempt number ${attempt.attempt_number} details`}
                                    onClick={() => {
                                      setSelectedExam(report);
                                      setSelectedAttempt(attempt);
                                      setModalOpen(true);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedExam(report);
                                        setSelectedAttempt(attempt);
                                        setModalOpen(true);
                                      }
                                    }}
                                  >
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{attempt.attempt_number}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{attempt.score_percentage}%</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                      {attempt.obtained_marks} / {attempt.max_marks}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        attempt.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                      }`}>
                                        {attempt.passed ? 'Passed' : 'Failed'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">{formatDate(attempt.attempt_date)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-center w-12">
                                      <button
                                        type="button"
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg-full bg-blue-50 text-[#111827] hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-md transition active:scale-95"
                                        title={`View Attempt #${attempt.attempt_number}`}
                                        aria-label={`View Attempt number ${attempt.attempt_number}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedExam(report);
                                          setSelectedAttempt(attempt);
                                          setModalOpen(true);
                                        }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                          <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                                        </svg>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Card className="text-center py-12">
              <div className="flex flex-col items-center justify-center">
                <div className="bg-gray-100 rounded-full p-4 mb-4">
                  <svg className="h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h2>
                <p className="text-gray-600 max-w-md">This student hasn't completed any exams yet or no results are available for this student.</p>
              </div>
            </Card>
          )
        ) : (
          <Card className="text-center py-12">
            <div className="flex flex-col items-center justify-center">
              <div className="bg-blue-100 rounded-full p-4 mb-4">
                <svg className="h-12 w-12 text-[#111827]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Select a Student</h2>
              <p className="text-gray-600 max-w-md">Choose a student from the dropdown above to view their exam results and performance metrics.</p>
            </div>
          </Card>
        )}
      </div>
      <ReportDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        exam={selectedExam}
        attempt={selectedAttempt}
      />
    </MainLayout>
  );
};

export default StudentResults; 
